import { CheckIcon, ChevronsUpDown, Flag as FlagIcon } from 'lucide-react'
import * as React from 'react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Button } from 'primitives/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'primitives/command'
import { Input, type InputProps } from 'primitives/base/input'
import { cn } from 'lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'

import { ScrollArea } from './scroll-area'
// Default grey flag icon component
const DefaultFlagIcon = () => <FlagIcon size={10} />

type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void
  }

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country]

  return (
    <span className="bg-foreground/20 flex items-center justify-center h-4 w-6 overflow-hidden rounded-sm">
      {Flag ? <Flag title={countryName} /> : <DefaultFlagIcon />}
    </span>
  )
}
FlagComponent.displayName = 'FlagComponent'

export const CountrySelect = ({ disabled, value, onChange, options }: CountrySelectProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [open, setOpen] = React.useState(false)
  const handleSelect = React.useCallback(
    (country: RPNInput.Country) => {
      setOpen(false)
      onChange(country)
    },
    [onChange],
  )

  return (
    <div ref={containerRef} className="min-w-[90px]">
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn('flex gap-1 rounded-md shadow-none px-3')}
            disabled={disabled}
          >
            <FlagComponent country={value} countryName={value} />
            <span className="flex-1 text-sm ml-1">{value}</span>
            <ChevronsUpDown
              className={cn('-mr-2 h-4 w-4 text-tertiary', disabled ? 'hidden' : 'opacity-100')}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandList>
              <CommandInput placeholder="Search country..." />
              <ScrollArea className="h-64">
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {options
                    .filter((x) => x.value)
                    .map((option) => (
                      <CommandItem
                        className="gap-2"
                        key={option.value}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <FlagComponent country={option.value} countryName={option.label} />
                        <span className="flex-1 text-sm">{option.label}</span>
                        {option.value && (
                          <span className="text-foreground/50 text-sm">
                            {option.value ? `+${RPNInput.getCountryCallingCode(option.value)}` : ''}
                          </span>
                        )}
                        <CheckIcon
                          className={cn(
                            'ml-auto h-4 w-4',
                            option.value === value ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input className={cn('rounded-e-lg rounded-s-none', className)} {...props} ref={ref} />
  ),
)
InputComponent.displayName = 'InputComponent'

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, ...props }, ref) => (
  <RPNInput.default
    ref={ref}
    className={cn('flex', className)}
    flagComponent={FlagComponent}
    countrySelectComponent={CountrySelect}
    inputComponent={InputComponent}
    /**
     * Handles the onChange event.
     *
     * react-phone-number-input might trigger the onChange event as undefined
     * when a valid phone number is not entered. To prevent this,
     * the value is coerced to an empty string.
     *
     * @param {E164Number | undefined} value - The entered value
     */
    onChange={(value) => onChange?.((value || '') as RPNInput.Value)}
    {...props}
  />
))
PhoneInput.displayName = 'PhoneInput'

type CountrySelectOption = { label: string; value: RPNInput.Country }

type CountrySelectProps = {
  disabled?: boolean
  value: RPNInput.Country
  onChange: (value: RPNInput.Country) => void
  options: CountrySelectOption[]
}

export { PhoneInput }
